
import { Link } from 'react-router-dom';
import Footer from '../../components/feature/Footer';

const lastUpdated = 'June 15, 2025';

const sections = [
  {
    title: 'Information We Collect',
    content: [
      'When you visit our website, we may collect certain information automatically, including your IP address, browser type, operating system, referring URLs, and browsing behaviour on our site.',
      'If you make a purchase or sign up for our newsletter, we collect personal information you provide directly, such as your name, email address, shipping address, and payment details. Payment information is processed securely through our third-party payment provider and is never stored on our servers.',
    ],
  },
  {
    title: 'How We Use Your Information',
    content: [
      'We use the information we collect to process and fulfil your orders, communicate with you about your purchases, send promotional emails if you have opted in, improve our website and product offerings, and comply with legal obligations.',
      'We will never sell, rent, or share your personal information with third parties for their own marketing purposes.',
    ],
  },
  {
    title: 'Cookies & Tracking',
    content: [
      'Our website uses cookies — small text files stored on your device — to enhance your browsing experience. These include essential cookies required for the site to function, analytics cookies that help us understand how visitors interact with our site, and preference cookies that remember your settings.',
      'You can manage or disable cookies through your browser settings at any time. Please note that disabling certain cookies may affect site functionality.',
    ],
  },
  {
    title: 'Data Retention',
    content: [
      'We retain your personal information only for as long as necessary to fulfil the purposes outlined in this policy, unless a longer retention period is required by law. Order records are kept for a minimum of six years for tax and accounting purposes.',
    ],
  },
  {
    title: 'Your Rights',
    content: [
      'Depending on your location, you may have the right to access the personal data we hold about you, request correction of inaccurate data, request deletion of your data, object to or restrict certain processing, and request data portability.',
      'To exercise any of these rights, please contact us using the details below. We will respond to your request within 30 days.',
    ],
  },
  {
    title: 'Third-Party Services',
    content: [
      'We may use third-party services for payment processing, shipping, email marketing, and website analytics. These providers have access only to the information necessary to perform their functions and are obligated to protect your data in accordance with applicable laws.',
    ],
  },
  {
    title: 'Data Security',
    content: [
      'We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.',
    ],
  },
  {
    title: 'Children\u2019s Privacy',
    content: [
      'Our website and products are not directed at individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected data from a child, we will take steps to delete it promptly.',
    ],
  },
  {
    title: 'Changes to This Policy',
    content: [
      'We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. Any updates will be posted on this page with a revised "Last Updated" date. We encourage you to review this policy periodically.',
    ],
  },
  {
    title: 'Contact Us',
    content: [
      'If you have any questions or concerns about this Privacy Policy or how we handle your data, please reach out to us at hello@ig-night.com. We are committed to resolving any issues promptly and transparently.',
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#0B1B3A]">
      {/* Header */}
      <header className="bg-[#0B1B3A] border-b border-[#162D52]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="text-2xl font-serif text-[#F5F0E8] cursor-pointer"
            style={{ fontFamily: 'Pacifico, serif' }}
          >
            ig-night
          </Link>
          <Link
            to="/"
            className="text-sm text-[#8A9BB5] hover:text-[#F5F0E8] transition-colors uppercase tracking-wider cursor-pointer flex items-center gap-2"
          >
            <i className="ri-arrow-left-line text-base"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs tracking-widest text-[#D4C5A9] uppercase mb-4">
            Legal
          </p>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#F5F0E8] mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Privacy Policy
          </h1>
          <p className="text-sm text-[#8A9BB5]">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-sm sm:text-base text-[#8A9BB5] leading-relaxed mb-12">
            At <strong className="text-[#F5F0E8]">ig-night</strong>, we respect
            your privacy and are committed to protecting the personal
            information you share with us. This Privacy Policy explains what
            data we collect, how we use it, and the choices you have regarding
            your information.
          </p>

          <div className="space-y-10">
            {sections.map((section, i) => (
              <article key={i}>
                <h4>
                  <a
                    href={`#section-${i}`}
                    id={`section-${i}`}
                    className="text-lg sm:text-xl font-serif text-[#F5F0E8] mb-4 block"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {i + 1}. {section.title}
                  </a>
                </h4>
                <div className="space-y-3">
                  {section.content.map((paragraph, j) => (
                    <p
                      key={j}
                      className="text-sm sm:text-base text-[#8A9BB5] leading-relaxed"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>

          {/* Back link */}
          <div className="mt-16 pt-8 border-t border-[#162D52] text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#D4C5A9] text-[#0B1B3A] px-6 py-3 rounded-md text-sm font-medium hover:bg-[#E8DCC4] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
